import React, { useMemo } from 'react';

import { di } from 'react-magnetic-di';

import { useChatModeStoreActions } from '@atlassian/conversation-assistant-chat-prompt-input/useChatModeStoreActions';
import {
	MessageActionsContainer,
	type MessageActionsContainerProps,
	type SendMessageConfirmedToolsParams,
} from '@atlassian/conversation-assistant-message-actions/controllers/message-actions-container';
import type { MessageActionResolutions } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { useStagingAreaState } from '@atlassian/conversation-assistant-store/controllers/staging-area';
import { useUserPreferences } from '@atlassian/conversation-assistant-store/controllers/user-preferences';

import { useGlobalConfig } from '../../../../common/utils/global-config/useGlobalConfig';
import { useUIConfig } from '../../../../common/utils/ui-config/useUIConfig';
import { useMessageActionsAnalytics } from '../../../../controllers/analytics/use-message-action-analytics';
import { useChatStoreActions } from '../../../../controllers/chat-store/useChatStoreActions';

type MessageActionProps = Omit<
	MessageActionsContainerProps,
	| 'cloudId'
	| 'orgId'
	| 'aaid'
	| 'productKey'
	| 'experienceId'
	| 'renderingTouchpoint'
	| 'isContentPreviewSupported'
	| 'messageActionsAnalytics'
	| 'sendMessageConfirmedTools'
	| 'resolveActionsBulk'
	| 'taskModeEnabled'
>;

export const MessageActions = (
	props: MessageActionProps,
): JSX.Element | (JSX.Element | null)[] | null => {
	di(MessageActionsContainer);

	const {
		siteId,
		orgId,
		productKey,
		experienceId,
		profileData: { aaid },
		renderingTouchpoint,
	} = useGlobalConfig();
	const { isContentPreviewSupported, browserContextSanitizer } = useUIConfig();
	const messageActionsAnalytics = useMessageActionsAnalytics({
		recipientAgentNamedId: props.recipientAgentNamedId,
		messageId: props.messageId,
	});
	const { sendMessageConfirmedTools, bulkResolveConversationUIActions } = useChatStoreActions();
	const { getChatMode } = useChatModeStoreActions();
	const {
		preferences: { storeUserMessages },
	} = useUserPreferences();
	const { messageIdSelectedForPreview, invocationIdSelectedForPreview } = useStagingAreaState();
	// A previewed draft can be tracked by either signal, so both must count as staged
	// content. The invocation-id path is used when the staging area is opened from a Rovo
	// chat preview card (e.g. Atlassian Home inline mode), where messageIdSelectedForPreview
	// stays null even though a draft is being previewed.
	const hasStagedContent =
		messageIdSelectedForPreview !== null || invocationIdSelectedForPreview !== null;

	// Snapshot the task-mode state at render time so renderers (e.g.
	// AskQuestionRenderer) can record whether Max mode was active when the
	// message that produced their action card was sent.
	const taskModeEnabled = getChatMode(props.conversationId ?? '').taskModeEnabled;

	const sendMessageConfirmedToolsWithModes = useMemo(() => {
		return (params: SendMessageConfirmedToolsParams) => {
			const chatMode = getChatMode(props.conversationId ?? '');
			return sendMessageConfirmedTools({
				...params,
				// TODO(ai_mate_store_messages_be_driven): remove this local storeUserMessages payload
				// value when BE user settings are fully rolled out; BE overrides it via the capability header.
				storeUserMessages,
				webSearchEnabled: chatMode.webSearchEnabled,
				deepResearchEnabled: chatMode.deepResearchEnabled,
				thinkDeeperEnabled: chatMode.thinkDeeperEnabled,
				alphaExtendedThinkingMode: chatMode.alphaExtendedThinkingMode,
				fastModeEnabled: chatMode.fastModeEnabled,
				taskModeEnabled: chatMode.taskModeEnabled,
				// Context-driving params used (behind the
				// `rovo-include-full-context-on-confirmed-tools` experiment) by the store action to
				// build the same full request context that `sendMessage` sends. Sourced the same way
				// the chat input sources them so tool confirmation matches a regular follow-up send.
				productKey,
				addCurrentPageAsContextUserPreference: chatMode.useCurrentPageContext,
				browserContextSanitizer,
				hasStagedContent,
			});
		};
	}, [
		sendMessageConfirmedTools,
		getChatMode,
		props.conversationId,
		storeUserMessages,
		productKey,
		browserContextSanitizer,
		hasStagedContent,
	]);

	// Resolves multiple UI actions in a single request (POST `/tools/resolution`) instead
	// of one `/action` call per invocation. Mirrors the payload shape used by
	// `MessageStreamActions`.
	const resolveActionsBulk = useMemo(
		() =>
			(
				actionKey: string,
				actionResolution: MessageActionResolutions,
				invocationIds: string[],
				invocationIdToCreatedIssueKey?: Record<string, string>,
			) => {
				bulkResolveConversationUIActions({
					conversationId: props.conversationId ?? '',
					messageId: props.messageId,
					payloads: invocationIds.map((invocationId) => ({
						context: {
							key: actionKey,
							invocationId,
							messageId: props.messageId,
							data: {
								...(invocationIdToCreatedIssueKey
									? { createdIssueKey: invocationIdToCreatedIssueKey[invocationId] }
									: {}),
							},
							resolution: {
								type: actionResolution,
							},
						},
					})),
					skipSuccessOptimisticAssistantResponse: true,
				});
			},
		[bulkResolveConversationUIActions, props.conversationId, props.messageId],
	);

	return (
		<MessageActionsContainer
			{...props}
			cloudId={siteId}
			orgId={orgId}
			aaid={aaid}
			productKey={productKey}
			experienceId={experienceId}
			renderingTouchpoint={renderingTouchpoint}
			isContentPreviewSupported={isContentPreviewSupported ?? false}
			messageActionsAnalytics={messageActionsAnalytics}
			sendMessageConfirmedTools={sendMessageConfirmedToolsWithModes}
			resolveActionsBulk={resolveActionsBulk}
			taskModeEnabled={taskModeEnabled}
		/>
	);
};
